import React, { useState } from "react";
import ConfirmationModal from "components/ConfirmationModal";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { FLOWABLE_DESIGN_URL, IS_DEV } from "globals/constants";
import { useDictionary } from "hooks";

import styles from "./styles.css";
import { api } from "services";
import { notifyError } from "utils";


const ImportExportButtons = ({ formModel }) => {
	const dictionary = useDictionary("formManager");

	const [ errorModalMessage, setErrorModalMessage ] = useState("");
	const [ showErrorModal, setShowErrorModal ] = useState(false);
	const [ showImportInfoModal, setShowImportInfoModal ] = useState(false);
	const [ showImportSuccessModal, setShowImportSuccessModal ] = useState(false);

	const fileInputElement = React.useRef(null);

	const downloadObjectAsFile = (object, fileName) => {
		try {
			const encodedDataString = `data:text/plain;charset=utf-8, ${obfuscate(object)}`;
			const downloadAnchorNode = document.createElement("a");
			downloadAnchorNode.setAttribute("href", encodedDataString);
			downloadAnchorNode.setAttribute("download", fileName);
			document.body.appendChild(downloadAnchorNode);
			downloadAnchorNode.click();
			downloadAnchorNode.remove();
		} catch(error) {
			console.error(error);
			showErrorDialog(dictionary.errors.obfuscate || "We are sorry! The form has not been exported due to an error!");
		}
	};

	const exportModel = async () => {
		const { modelId, name } = formModel;

		try {
			const model = await api.form.getSimplifiedModel(modelId);
			const fileName = `${name.toLowerCase()}.cayuse`;

			downloadObjectAsFile(model, fileName);

		} catch(error) {
			const message = IS_DEV ? error.message : dictionary.errors.obfuscate;
			notifyError(message, error);
		}
	};

	const handleFileInputChange = event => {
		const reader = new FileReader();

		reader.addEventListener("load", event => {
			try {
				const formDefinition = deobfuscate(event.target.result);
				importModel(formDefinition);
			} catch {
				showErrorDialog(dictionary.errors.invalidContent || "We are sorry! The form has not been imported due to an invalid file!");
			}
		});

		const file = event?.target?.files && event.target.files[0];

		if (file) {
			reader.readAsText(file, "UTF-8");
		}
	};

	const handleExportButtonClick = () => {
		exportModel();
	};

	const handleImportButtonClick = () => {
		showImportInfoDialog();
	};

	const importModel = async formDefinition => {
		const { modelId, modelKey } = formModel;

		try {
			// Validate type
			if (formDefinition.metadata.key !== modelKey) {
				throw dictionary.errors.invalidType || "We are sorry! The form has not been imported due to a type mismatch!";
			}

			await api.form.updateSimplifiedModel(modelId, formDefinition);

			showImportSuccessDialog();

		} catch(error) {
			showErrorDialog(error);
		}
	};

	const obfuscate = object => {
		return window.btoa(unescape(encodeURIComponent(JSON.stringify(object))));
	};

	const deobfuscate = string => {
		return JSON.parse(decodeURIComponent(escape(window.atob(string))));
	};

	const onConfirmError = () => {
		setShowErrorModal(false);
	};

	const onConfirmImport = isConfirmed => {
		if (isConfirmed) {
			fileInputElement.current.click();
		}

		setShowImportInfoModal(false);
	};

	const onConfirmImportSuccess = () => {
		setShowImportSuccessModal(false);
	};

	const showErrorDialog = errorMessage => {
		setErrorModalMessage(errorMessage);
		setShowErrorModal(true);
	};

	const showImportInfoDialog = () => {
		setShowImportInfoModal(true);
	};

	const showImportSuccessDialog = () => {
		setShowImportSuccessModal(true);
	};

	return (
		<>
			<ConfirmationModal
				icon={"warning circle"}
				message={errorModalMessage}
				title={dictionary.importErrorModalTitle || "Error"}
				visible={showErrorModal}
				onConfirm={null}
				onCancel={() => onConfirmError(false)}
			/>
			<ConfirmationModal
				confirmButtonLabel={dictionary.importInfoModalConfirmButtonLabel || "Okay"}
				icon={"upload"}
				message={dictionary.importInfoModalMessage || "Would you like to import a new form? This will override the in-draft revision."}
				title={dictionary.importInfoModalTitle || "Form Import"}
				visible={showImportInfoModal}
				onConfirm={() => onConfirmImport(true)}
				onCancel={() => onConfirmImport(false)}
			/>
			<ConfirmationModal
				confirmButtonLabel={dictionary.importSuccessModalConfirmButtonLabel || "Okay"}
				icon={"upload"}
				message={dictionary.importSuccessModalMessage || "The form has been imported successfully."}
				title={dictionary.importSuccessModalTitle || "Form Import"}
				visible={showImportSuccessModal}
				onConfirm={() => onConfirmImportSuccess(true)}
				onCancel={null}
			/>
			<div className={styles.buttonSet}>
				<IconButton
					icon="download"
					onClick={handleExportButtonClick}
					aria-label={dictionary.downloadButtonAriaLabel || "Download form"}
				>
				</IconButton>
				<IconButton
					icon="upload"
					onClick={handleImportButtonClick}
					aria-label={dictionary.uploadButtonAriaLabel || "Upload form"}
				>
				</IconButton>
				<input
					accept=".cayuse"
					className={styles.fileInput}
					ref={fileInputElement}
					type="file"
					onChange={event => handleFileInputChange(event)}
				/>
			</div>
		</>
	);
};

export default ImportExportButtons;
